import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RecordModal from './RecordModal';
import pocketbaseService from '../services/pocketbase';

const { FiArrowLeft, FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiDatabase, FiBox } = FiIcons;

const CollectionView = ({ collection, onBack }) => {
  const [records, setRecords] = useState([]);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadCollectionData(); }, [collection.name]);

  const loadCollectionData = async () => {
    try {
      setLoading(true);
      const [schemaData, recordsData] = await Promise.all([
        pocketbaseService.getCollectionSchema(collection.name),
        pocketbaseService.getRecords(collection.name, 1)
      ]);
      setSchema(schemaData);
      setRecords(recordsData.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;
    return Object.values(record).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()));
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8 sm:mb-10">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-3 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-indigo-600 shadow-sm transition-all">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-black text-slate-900 truncate uppercase tracking-tight flex items-center">
              <SafeIcon icon={FiDatabase} className="mr-2 text-indigo-600" /> {collection.name}
            </h1>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">{records.length} Objetos</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-11 pr-4 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-xs outline-none focus:border-indigo-500 shadow-sm" />
          </div>
          <button onClick={() => { setSelectedRecord(null); setModalMode('create'); }} className="p-4 bg-slate-900 text-white rounded-2xl shadow-lg hover:bg-black">
            <SafeIcon icon={FiPlus} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecords.map(record => (
            <div key={record.id} className="bg-white border border-slate-100 p-5 rounded-[2rem] shadow-sm hover:border-indigo-200 transition-all flex flex-col justify-between group">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[8px] font-mono font-black text-indigo-400 uppercase tracking-widest bg-indigo-50 px-2 py-0.5 rounded">ID: {record.id.substring(0,8)}</span>
                  <div className="flex space-x-1">
                    <button onClick={() => { setSelectedRecord(record); setModalMode('view'); }} className="p-2 text-slate-300 hover:text-indigo-600"><SafeIcon icon={FiEye} className="text-xs" /></button>
                    <button onClick={() => { setSelectedRecord(record); setModalMode('edit'); }} className="p-2 text-slate-300 hover:text-blue-600"><SafeIcon icon={FiEdit} className="text-xs" /></button>
                    <button onClick={async () => { if(confirm('Excluir registro?')) { await pocketbaseService.deleteRecord(collection.name, record.id); loadCollectionData(); } }} className="p-2 text-slate-300 hover:text-red-600"><SafeIcon icon={FiTrash2} className="text-xs" /></button>
                  </div>
                </div>
                <div className="space-y-2">
                  {schema.slice(0, 3).map(f => (
                    <div key={f.name} className="flex flex-col">
                      <span className="text-[8px] font-black text-slate-300 uppercase tracking-tighter">{f.name}</span>
                      <span className="text-[10px] font-bold text-slate-700 truncate">
                        {typeof record[f.name] === 'object' ? 'JSON object' : String(record[f.name] || '-')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => { setSelectedRecord(record); setModalMode('edit'); }} className="w-full py-3 bg-slate-50 text-slate-400 rounded-xl text-[9px] font-black uppercase tracking-widest group-hover:bg-indigo-600 group-hover:text-white transition-all">
                Abrir Contexto
              </button>
            </div>
          ))}
        </div>
      )}

      {modalMode && (
        <RecordModal mode={modalMode} collection={collection} record={selectedRecord} schema={schema} onClose={async (reload) => { setModalMode(null); if(reload) await loadCollectionData(); }} />
      )}
    </div>
  );
};

export default CollectionView;